/**
 * Pipeline (Kanban) board. Groups applications by stage into fixed columns.
 * Dragging a card to another column requests a stage move (opens the reason
 * dialog) — the card is NOT moved optimistically; the parent refetches after
 * the API confirms.
 */

import React, { useMemo, useState } from 'react';
import { cn } from '../../../utils/cn';
import type { ApplicationRow, Stage } from '../../../services/recruitmentApi';
import { PIPELINE_STAGES, STAGE_META } from '../config/constants';
import { SourcePill } from './badges';
import { initials } from '../utils/format';

interface PipelineBoardProps {
	rows: ApplicationRow[];
	onOpen: (applicationId: number) => void;
	onRequestMove: (application: ApplicationRow, to: Stage) => void;
}

const daysInStage = (updatedAt?: string): number | null => {
	if (!updatedAt) return null;
	const t = new Date(updatedAt).getTime();
	if (Number.isNaN(t)) return null;
	return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

export const PipelineBoard: React.FC<PipelineBoardProps> = ({ rows, onOpen, onRequestMove }) => {
	const [dragId, setDragId] = useState<number | null>(null);
	const [overStage, setOverStage] = useState<Stage | null>(null);

	const grouped = useMemo(() => {
		const map = new Map<Stage, ApplicationRow[]>();
		PIPELINE_STAGES.forEach(s => map.set(s, []));
		rows.forEach(r => {
			const bucket = map.get(r.stage as Stage);
			if (bucket) bucket.push(r);
			else map.set(r.stage as Stage, [r]); // tolerate unexpected stage values
		});
		return map;
	}, [rows]);

	const handleDrop = (target: Stage) => {
		setOverStage(null);
		const app = rows.find(r => r.id === dragId);
		setDragId(null);
		if (!app) return;
		if (app.stage === target) return; // no-op move; API would reject it
		onRequestMove(app, target);
	};

	return (
		<div className='pb-2'>
			<div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
				{PIPELINE_STAGES.map(stage => {
					const items = grouped.get(stage) ?? [];
					const meta = STAGE_META[stage];
					const isOver = overStage === stage;
					return (
						<div
							key={stage}
							className={cn(
								'flex min-h-[9rem] flex-col rounded-xl border bg-gray-50/60 transition-colors',
								isOver ? 'border-primary bg-primary-50/50' : 'border-gray-200'
							)}
							onDragOver={e => {
								e.preventDefault();
								if (overStage !== stage) setOverStage(stage);
							}}
							onDragLeave={() => setOverStage(prev => (prev === stage ? null : prev))}
							onDrop={() => handleDrop(stage)}
						>
							<div className={cn('flex items-center justify-between rounded-t-xl border-t-2 px-3 py-2.5', meta.tone.accent)}>
								<span className='text-xs font-semibold uppercase tracking-wide text-gray-700'>{meta.label}</span>
								<span className='rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-500'>
									{items.length}
								</span>
							</div>

							<div className='flex flex-1 flex-col gap-2 p-2'>
								{items.length === 0 ? (
									<div className='rounded-lg border border-dashed border-gray-200 py-6 text-center text-xs text-gray-400'>
										Drop here
									</div>
								) : (
									items.map(app => {
										const d = daysInStage(app.updated_at);
										return (
											<button
												key={app.id}
												type='button'
												draggable
												onDragStart={() => setDragId(app.id)}
												onDragEnd={() => {
													setDragId(null);
													setOverStage(null);
												}}
												onClick={() => onOpen(app.id)}
												className={cn(
													'cursor-grab rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-primary/40 hover:shadow active:cursor-grabbing',
													dragId === app.id && 'opacity-50'
												)}
											>
												<p className='truncate text-sm font-semibold text-gray-900'>{app.candidate_name}</p>
												<p className='mt-0.5 truncate text-xs text-gray-500'>{app.job_title}</p>
												<div className='mt-2 flex items-center justify-between'>
													<SourcePill source={app.source} />
													<div className='flex items-center gap-2'>
														{d !== null && <span className='text-[11px] text-gray-400'>{d}d</span>}
														{(app.recruiter_first_name || app.recruiter_last_name) && (
															<span
																className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-[10px] font-semibold text-primary-700'
																title={`${app.recruiter_first_name ?? ''} ${app.recruiter_last_name ?? ''}`.trim()}
															>
																{initials(`${app.recruiter_first_name ?? ''} ${app.recruiter_last_name ?? ''}`)}
															</span>
														)}
													</div>
												</div>
											</button>
										);
									})
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
